import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Package, TrendUp, ChartLineDown, Warning, Trash, Plus, Calculator, Check, X, Sparkle, UploadSimple } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { formatINR } from "../../lib/format";

// Procurement Intelligence & Proposal Engine — 4 tabs backed by /api/procurement
const ADDON_TYPE_LABELS = {
  site_prereq: "Site Pre-requisite",
  consumable: "Operational Consumable",
  install: "Install & Commission",
  amc: "AMC / CMC",
};

export default function Procurement() {
  const [tab, setTab] = useState("dashboard"); // dashboard | items | proposal | bom
  const [intel, setIntel] = useState({ items: [] });
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const loadIntel = () => api.get("/procurement/intelligence").then((r) => setIntel(r.data || { items: [] })).catch(() => {});
  const loadItems = () => api.get("/procurement/items").then((r) => setItems(r.data || [])).catch(() => {});

  useEffect(() => { loadIntel(); loadItems(); }, []);

  const filteredIntel = useMemo(() => {
    if (!search) return intel.items;
    const s = search.toLowerCase();
    return intel.items.filter((r) =>
      (r.item.name || "").toLowerCase().includes(s) || (r.item.code || "").toLowerCase().includes(s) || (r.item.category || "").toLowerCase().includes(s)
    );
  }, [intel, search]);

  return (
    <Layout title="Procurement Intelligence" subtitle="Vendor pricing · Client history · Proposal margin engine">
      <div className="flex gap-1 border-b border-black/10 mb-6 flex-wrap">
        {[
          { k: "dashboard", l: "📊 Intelligence Dashboard", tid: "tab-proc-dashboard" },
          { k: "items", l: `📦 Item Master (${items.length})`, tid: "tab-proc-items" },
          { k: "proposal", l: "🎯 Proposal Builder", tid: "tab-proc-proposal" },
          { k: "bom", l: "🔧 BOM / Add-ons Map", tid: "tab-proc-bom" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={t.tid}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.k ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <DashboardTab intel={filteredIntel} search={search} setSearch={setSearch} onRefresh={loadIntel} />
      )}
      {tab === "items" && (
        <ItemsTab items={items} onReload={() => { loadItems(); loadIntel(); }} busy={busy} setBusy={setBusy} />
      )}
      {tab === "proposal" && (
        <ProposalTab items={items} />
      )}
      {tab === "bom" && (
        <BomTab items={items} />
      )}
    </Layout>
  );
}

// ---------- INTELLIGENCE DASHBOARD ----------
function DashboardTab({ intel, search, setSearch, onRefresh }) {
  return (
    <div className="space-y-4" data-testid="proc-dashboard">
      <div className="flex items-center gap-3">
        <input placeholder="Search item name / code / category…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="proc-search" className="flex-1 border border-black/15 rounded-none px-3 py-2 text-sm max-w-md"/>
        <button onClick={onRefresh} className="px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black hover:text-white">↻ Refresh</button>
      </div>
      {intel.length === 0 ? (
        <div className="border border-black/10 bg-white p-8 text-center text-sm text-[#717680]">
          No items yet. Add items under <b>Item Master</b> tab, then create a purchase or import — vendor prices auto-populate.
        </div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#FAFBFC] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
              <tr>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 text-right">Latest Buy</th>
                <th className="px-3 py-2 text-right">Lowest Buy</th>
                <th className="px-3 py-2 text-right">Avg Buy</th>
                <th className="px-3 py-2 text-right">Last Sale</th>
                <th className="px-3 py-2 text-right">Margin</th>
                <th className="px-3 py-2">Best Vendor</th>
              </tr>
            </thead>
            <tbody>
              {intel.map((r) => (
                <tr key={r.item.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`intel-row-${r.item.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.item.name}</div>
                    <div className="text-[10px] text-[#717680] font-mono-shelv">{r.item.code} · {r.item.category || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-shelv">{r.latest_purchase_price != null ? formatINR(r.latest_purchase_price) : "—"}<div className="text-[10px] text-[#717680]">{r.latest_purchase_date || ""}</div></td>
                  <td className="px-3 py-2 text-right font-mono-shelv text-[#0D9488]">{r.lowest_purchase_price != null ? formatINR(r.lowest_purchase_price) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-shelv">{r.avg_purchase_price != null ? formatINR(r.avg_purchase_price) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-shelv">{r.latest_sale ? formatINR(r.latest_sale.unit_sold_price) : "—"}<div className="text-[10px] text-[#717680]">{r.latest_sale?.client_name || ""}</div></td>
                  <td className="px-3 py-2 text-right font-mono-shelv" style={{ color: r.current_margin_pct != null && r.current_margin_pct < 15 ? "#D90429" : "#0D9488" }}>
                    {r.current_margin_pct != null ? `${r.current_margin_pct}%` : "—"}
                    {r.current_margin_pct != null && r.current_margin_pct < 15 && <Warning size={11} className="inline ml-1" weight="fill"/>}
                  </td>
                  <td className="px-3 py-2">
                    {r.best_vendor ? (
                      <div className="border-l-2 border-[#0D9488] pl-2">
                        <div className="text-xs font-semibold">{r.best_vendor.vendor_name}</div>
                        <div className="text-[10px] text-[#717680]">{formatINR(r.best_vendor.unit_price)} · {r.best_vendor.purchase_date}</div>
                      </div>
                    ) : <span className="text-[#717680] text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- ITEM MASTER ----------
function ItemsTab({ items, onReload, busy, setBusy }) {
  const [form, setForm] = useState({ code: "", name: "", category: "", description: "", base_price: 0, warranty_months: 12 });
  const [editing, setEditing] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const aiFileRef = useRef(null);

  const aiFetch = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAiBusy(true); setAiResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", "vendor_quotation");
      const { data } = await api.post("/procurement/items/ai-fetch", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      setAiResult(data);
      toast.success(`AI extracted ${data.items_created} new item(s) + ${data.vendor_rows_added} vendor price row(s) from ${data.vendor_name}`);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI fetch failed");
    } finally {
      setAiBusy(false);
      if (aiFileRef.current) aiFileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code + name required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/procurement/items/${editing.id}`, form);
      else await api.post("/procurement/items", form);
      toast.success(editing ? "Updated" : "Added");
      setForm({ code: "", name: "", category: "", description: "", base_price: 0, warranty_months: 12 });
      setEditing(null);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this item?")) return;
    try { await api.delete(`/procurement/items/${id}`); toast.success("Removed"); onReload(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="proc-items-tab">
      {/* AI Auto-Fetch — extract items + vendor prices from quotations/invoices */}
      <div className="border-2 border-dashed border-[#D90429]/40 bg-gradient-to-br from-[#FFE5E9]/40 to-white p-4" data-testid="proc-ai-fetch">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#D90429] text-white"><Sparkle size={18} weight="fill"/></div>
          <div className="flex-1">
            <div className="font-display font-black text-base tracking-tight">AI Auto-Fetch · Build your item repository in seconds</div>
            <div className="text-xs text-[#4A4D57] mt-1">
              Drop any vendor quotation, purchase invoice, or price list (PDF / Excel / CSV). Gemini reads the layout, extracts vendor name + every line item's code/name/price/MOQ/lead time, and:
              <ul className="list-disc ml-5 mt-1">
                <li>Auto-creates new Item Master rows (skips duplicates by code/name)</li>
                <li>Adds each item's price to the Vendor Price Ledger so intelligence dashboards update instantly</li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <input ref={aiFileRef} type="file" accept=".pdf,.xlsx,.xls,.csv" onChange={aiFetch} disabled={aiBusy} className="hidden" data-testid="ai-fetch-input"/>
            <button
              onClick={() => aiFileRef.current?.click()}
              disabled={aiBusy}
              data-testid="ai-fetch-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C] disabled:opacity-40"
            >
              <UploadSimple size={12} weight="bold"/> {aiBusy ? "AI parsing…" : "Upload & extract"}
            </button>
            <div className="text-[10px] text-[#717680]">PDF · Excel · CSV · ≤ 15MB</div>
          </div>
        </div>
        {aiResult && (
          <div className="mt-3 border border-black/10 bg-white p-3 text-xs" data-testid="ai-fetch-result">
            <div className="font-semibold text-[#0D9488] mb-1">✓ Extraction complete</div>
            <div>Vendor: <b>{aiResult.vendor_name}</b> · PO: <span className="font-mono-shelv">{aiResult.po_number}</span> · Date: {aiResult.purchase_date}</div>
            <div className="mt-1 text-[#4A4D57]">
              {aiResult.items_created} new item(s) created · {aiResult.items_reused} matched existing items · {aiResult.vendor_rows_added} vendor price row(s) added
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 border border-black/10 bg-white p-4">
        <div className="font-display font-bold mb-3">{editing ? `Edit · ${editing.code}` : "New item"}</div>
        <div className="space-y-2">
          <input placeholder="Item code (e.g. HEMA-500)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="itm-code" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>
          <input placeholder="Item name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="itm-name" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>
          <input placeholder="Category (Analyzer, Chiller, …)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="itm-cat" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" placeholder="Base list price (₹)" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: Number(e.target.value) })} data-testid="itm-price" className="border border-black/15 rounded-none px-3 py-2 text-sm"/>
            <input type="number" placeholder="Warranty (months)" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: Number(e.target.value) })} className="border border-black/15 rounded-none px-3 py-2 text-sm"/>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={busy} data-testid="itm-save" className="flex-1 bg-[#D90429] text-white px-4 py-2 text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40">
              <Plus size={12} className="inline mr-1"/> {editing ? "Update" : "Add item"}
            </button>
            {editing && <button onClick={() => { setEditing(null); setForm({ code: "", name: "", category: "", description: "", base_price: 0, warranty_months: 12 }); }} className="px-3 py-2 border border-black/15 text-sm">Cancel</button>}
          </div>
        </div>
      </div>
      <div className="lg:col-span-7 border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFC] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
            <tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Category</th><th className="px-3 py-2 text-right">Base ₹</th><th className="px-3 py-2 text-right"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-[#717680]">No items yet.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`itm-row-${it.id}`}>
                <td className="px-3 py-2 font-mono-shelv text-xs">{it.code}</td>
                <td className="px-3 py-2 font-semibold">{it.name}<div className="text-[10px] text-[#717680]">{it.description?.slice(0, 40)}</div></td>
                <td className="px-3 py-2 text-xs">{it.category || "—"}</td>
                <td className="px-3 py-2 text-right font-mono-shelv">{formatINR(it.base_price)}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => { setEditing(it); setForm({ code: it.code, name: it.name, category: it.category, description: it.description || "", base_price: it.base_price, warranty_months: it.warranty_months }); }} className="text-xs text-[#0D9488] hover:underline mr-2">Edit</button>
                  <button onClick={() => remove(it.id)} className="text-xs text-[#D90429] hover:underline"><Trash size={10} className="inline"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

// ---------- PROPOSAL BUILDER ----------
function ProposalTab({ items }) {
  const [itemId, setItemId] = useState("");
  const [clientName, setClientName] = useState("");
  const [proposedPrice, setProposedPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [drawer, setDrawer] = useState(null);
  const [addonState, setAddonState] = useState({}); // {bom_id: {included, price}}
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (!itemId) { setDrawer(null); setAddonState({}); setEstimate(null); return; }
    api.get(`/procurement/items/${itemId}/drawer`, { params: { client_name: clientName } }).then((r) => {
      setDrawer(r.data);
      // Seed add-on selections from server defaults
      const st = {};
      (r.data.addons || []).forEach((a) => { st[a.id] = { included: a.required_default, price: a.tentative_price, name: a.addon_name, addon_type: a.addon_type }; });
      setAddonState(st);
    }).catch(() => {});
  }, [itemId, clientName]);

  // Live estimate
  useEffect(() => {
    if (!itemId || proposedPrice <= 0) { setEstimate(null); return; }
    const addons = Object.entries(addonState).map(([bom_id, v]) => ({ bom_id, name: v.name, addon_type: v.addon_type, price: Number(v.price) || 0, included: !!v.included }));
    const t = setTimeout(() => {
      api.post("/procurement/proposal/estimate", { main_item_id: itemId, client_name: clientName, proposed_unit_price: Number(proposedPrice), quantity: Number(quantity) || 1, addons })
        .then((r) => setEstimate(r.data)).catch(() => setEstimate(null));
    }, 300);
    return () => clearTimeout(t);
  }, [itemId, clientName, proposedPrice, quantity, addonState]);

  const toggleAddon = (id) => setAddonState((s) => ({ ...s, [id]: { ...s[id], included: !s[id].included } }));
  const setAddonPrice = (id, price) => setAddonState((s) => ({ ...s, [id]: { ...s[id], price } }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="proc-proposal-tab">
      {/* Left: Selection */}
      <div className="lg:col-span-4 space-y-3">
        <div className="border border-black/10 bg-white p-4">
          <label className="block text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680] mb-1">Main machine</label>
          <select value={itemId} onChange={(e) => setItemId(e.target.value)} data-testid="prop-item-select" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm">
            <option value="">Select…</option>
            {items.map((it) => <option key={it.id} value={it.id}>{it.code} · {it.name}</option>)}
          </select>

          <label className="block text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680] mb-1 mt-3">Client</label>
          <input placeholder="e.g. Apollo Hospitals" value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="prop-client" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <div>
              <label className="block text-[10px] font-mono-shelv uppercase text-[#717680] mb-1">Qty</label>
              <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} data-testid="prop-qty" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono-shelv uppercase text-[#717680] mb-1">Sell price / unit</label>
              <input type="number" min={0} value={proposedPrice} onChange={(e) => setProposedPrice(Number(e.target.value))} data-testid="prop-price" className="w-full border border-black/15 rounded-none px-3 py-2 text-sm"/>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Drawer + Estimate */}
      <div className="lg:col-span-8 space-y-4">
        {!drawer ? (
          <div className="border border-black/10 bg-white p-8 text-center text-sm text-[#717680]">
            Select a machine to see its Pricing & Margin Intelligence Drawer.
          </div>
        ) : (
          <>
            {/* Pricing intelligence */}
            <div className="border border-black/10 bg-white" data-testid="prop-drawer">
              <div className="px-4 py-2 border-b border-black/10 bg-[#0A0A0A] text-white">
                <div className="overline text-[10px] text-[#F4A261]">Pricing & Margin Intelligence</div>
                <div className="font-display font-bold">{drawer.item.name}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 text-xs">
                <div className="p-3 border-r border-black/5">
                  <div className="overline text-[10px]">Last Purchased At</div>
                  <div className="font-mono-shelv text-lg font-bold">{drawer.last_purchase ? formatINR(drawer.last_purchase.unit_price) : "—"}</div>
                  <div className="text-[10px] text-[#717680]">{drawer.last_purchase ? `${drawer.last_purchase.vendor_name} · ${drawer.last_purchase.purchase_date}` : "no purchase history"}</div>
                </div>
                <div className="p-3 border-r border-black/5">
                  <div className="overline text-[10px]">Last Sold to {clientName || "This Client"}</div>
                  <div className="font-mono-shelv text-lg font-bold">{drawer.last_sale_to_this_client ? formatINR(drawer.last_sale_to_this_client.unit_sold_price) : "—"}</div>
                  <div className="text-[10px] text-[#717680]">{drawer.last_sale_to_this_client ? drawer.last_sale_to_this_client.sale_date : (clientName ? "no history with this client" : "enter client name")}</div>
                </div>
                <div className="p-3">
                  <div className="overline text-[10px]">Last Sale (Any Client)</div>
                  <div className="font-mono-shelv text-lg font-bold">{drawer.last_sale_overall ? formatINR(drawer.last_sale_overall.unit_sold_price) : "—"}</div>
                  <div className="text-[10px] text-[#717680]">{drawer.last_sale_overall ? `${drawer.last_sale_overall.client_name} · ${drawer.last_sale_overall.sale_date}` : "—"}</div>
                </div>
              </div>
              {estimate && (
                <div className="border-t border-black/10 p-4 bg-[#FAFBFC] flex items-center justify-between" data-testid="prop-margin-bar">
                  <div>
                    <div className="overline text-[10px]">Gross margin</div>
                    <div className="text-2xl font-display font-black tracking-tighter" style={{ color: estimate.margin_flag === "below_healthy" ? "#D90429" : "#0D9488" }}>
                      {estimate.main.margin_pct != null ? `${estimate.main.margin_pct}%` : "—"}
                    </div>
                  </div>
                  {estimate.margin_flag === "below_healthy" && (
                    <div className="px-3 py-2 bg-[#D90429] text-white text-xs font-mono-shelv uppercase tracking-widest flex items-center gap-1" data-testid="prop-margin-flag">
                      <Warning size={12} weight="fill"/> Below 15% healthy threshold
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Add-ons */}
            <div className="border border-black/10 bg-white" data-testid="prop-addons">
              <div className="px-4 py-2 border-b border-black/10 flex items-center gap-2 bg-[#FAFBFC]">
                <div className="overline text-[10px] text-[#D90429]">Add-ons & Readiness</div>
                <span className="text-xs text-[#717680]">· {drawer.addons.length} mapped</span>
              </div>
              {drawer.addons.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#717680]">No add-ons mapped for this machine. Add them under the BOM tab.</div>
              ) : (
                <div className="divide-y divide-black/5">
                  {drawer.addons.map((a) => {
                    const st = addonState[a.id] || {};
                    return (
                      <div key={a.id} className={`flex items-center gap-3 px-4 py-2 ${st.included ? "bg-[#FFE5E9]/30" : ""}`}>
                        <input type="checkbox" checked={!!st.included} onChange={() => toggleAddon(a.id)} data-testid={`prop-addon-${a.id}`}/>
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{a.addon_name}</div>
                          <div className="text-[10px] text-[#717680] uppercase tracking-widest font-mono-shelv">{ADDON_TYPE_LABELS[a.addon_type] || a.addon_type}{a.required_default ? " · required by default" : ""}</div>
                        </div>
                        <input type="number" value={st.price ?? a.tentative_price} onChange={(e) => setAddonPrice(a.id, Number(e.target.value))} className="w-32 border border-black/15 rounded-none px-2 py-1 text-sm font-mono-shelv text-right"/>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Totals */}
            {estimate && (
              <div className="border border-black/10 bg-white p-4" data-testid="prop-totals">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <Stat label="Main line" value={formatINR(estimate.main.line_total)}/>
                  <Stat label={`Add-ons (${estimate.addons_included.length})`} value={formatINR(estimate.addon_total)}/>
                  <Stat label="Grand total" value={formatINR(estimate.grand_total)} highlight/>
                  <Stat label="Overall margin" value={estimate.overall_margin_pct != null ? `${estimate.overall_margin_pct}%` : "—"} color={estimate.overall_margin_pct != null && estimate.overall_margin_pct < 15 ? "#D90429" : "#0D9488"}/>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
function Stat({ label, value, highlight, color }) {
  return (
    <div>
      <div className="overline text-[10px] text-[#717680]">{label}</div>
      <div className={`font-display ${highlight ? "text-2xl" : "text-lg"} font-black tracking-tight`} style={{ color: color || "#0A0A0A" }}>{value}</div>
    </div>
  );
}

// ---------- BOM MAP ----------
function BomTab({ items }) {
  const [parentId, setParentId] = useState("");
  const [bomRows, setBomRows] = useState([]);
  const [form, setForm] = useState({ addon_name: "", addon_type: "consumable", tentative_price: 0, required_default: false });

  const load = () => {
    if (!parentId) { setBomRows([]); return; }
    api.get("/procurement/bom", { params: { parent_item_id: parentId } }).then((r) => setBomRows(r.data || []));
  };
  useEffect(() => { load(); }, [parentId]);

  const add = async () => {
    if (!parentId) { toast.error("Pick a parent machine first"); return; }
    if (!form.addon_name.trim()) { toast.error("Add-on name required"); return; }
    try {
      await api.post("/procurement/bom", { parent_item_id: parentId, ...form });
      setForm({ addon_name: "", addon_type: "consumable", tentative_price: 0, required_default: false });
      toast.success("Added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/procurement/bom/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="proc-bom-tab">
      <div className="border border-black/10 bg-white p-4">
        <label className="block text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680] mb-1">Parent machine</label>
        <select value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="bom-parent-select" className="w-full max-w-md border border-black/15 rounded-none px-3 py-2 text-sm">
          <option value="">Select…</option>
          {items.map((it) => <option key={it.id} value={it.id}>{it.code} · {it.name}</option>)}
        </select>
      </div>

      {parentId && (
        <div className="border border-black/10 bg-white p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <input placeholder="Add-on name" value={form.addon_name} onChange={(e) => setForm({ ...form, addon_name: e.target.value })} data-testid="bom-name" className="border border-black/15 rounded-none px-3 py-2 text-sm md:col-span-2"/>
            <select value={form.addon_type} onChange={(e) => setForm({ ...form, addon_type: e.target.value })} data-testid="bom-type" className="border border-black/15 rounded-none px-3 py-2 text-sm">
              <option value="site_prereq">Site Pre-requisite</option>
              <option value="consumable">Consumable</option>
              <option value="install">Install & Commission</option>
              <option value="amc">AMC / CMC</option>
            </select>
            <input type="number" placeholder="₹ tentative" value={form.tentative_price} onChange={(e) => setForm({ ...form, tentative_price: Number(e.target.value) })} className="border border-black/15 rounded-none px-3 py-2 text-sm"/>
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={form.required_default} onChange={(e) => setForm({ ...form, required_default: e.target.checked })}/>
              required by default
            </label>
            <button onClick={add} data-testid="bom-add" className="bg-[#D90429] text-white px-3 py-2 text-sm font-semibold md:col-span-5"><Plus size={12} className="inline mr-1"/> Add add-on</button>
          </div>
        </div>
      )}

      {bomRows.length > 0 && (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFC] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
              <tr><th className="px-3 py-2">Add-on</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Tentative ₹</th><th className="px-3 py-2">Required by default</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {bomRows.map((b) => (
                <tr key={b.id} className="border-t border-black/5" data-testid={`bom-row-${b.id}`}>
                  <td className="px-3 py-2 font-semibold">{b.addon_name}</td>
                  <td className="px-3 py-2 text-xs">{ADDON_TYPE_LABELS[b.addon_type] || b.addon_type}</td>
                  <td className="px-3 py-2 text-right font-mono-shelv">{formatINR(b.tentative_price)}</td>
                  <td className="px-3 py-2 text-xs">{b.required_default ? <Check size={12} className="inline text-[#0D9488]"/> : <X size={12} className="inline text-[#717680]"/>}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => remove(b.id)} className="text-xs text-[#D90429] hover:underline"><Trash size={10} className="inline"/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
